import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchGoals, patchGoals, submitActions as submitActionsApi, fetchGoalsChangelog, postGoalsChangelog } from '../api/goalsApi';
import { useCanEditStylist } from '../hooks/useUserRole';

const GOALS_KEY = (uid) => `kuroco_goals_${uid}`;
const PROFILE_KEY = (uid) => `kuroco_profile_${uid}`;
const NOTIF_KEY = (uid) => `kuroco_notif_${uid}`;

/** Cross-hook sync: AI chat ↔ Goals tab share the same Firestore goals snapshot. */
export const GOALS_UPDATED_EVENT = 'kuroco-goals-updated';

export function notifyGoalsUpdated(goals) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(GOALS_UPDATED_EVENT, { detail: { goals } }));
}

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* ignore quota errors */
  }
}

export function loadGoalsLocal(uid) {
  if (!uid) return null;
  return readJson(GOALS_KEY(uid));
}

export function saveGoalsLocal(uid, data) {
  if (!uid) return;
  writeJson(GOALS_KEY(uid), data);
}

export function loadProfileLocal(uid) {
  if (!uid) return null;
  return readJson(PROFILE_KEY(uid));
}

export function saveProfileLocal(uid, data) {
  if (!uid) return;
  writeJson(PROFILE_KEY(uid), data);
}

export function loadNotifLocal(uid) {
  // OFF until the user turns it on (which asks for permission) — the server is the truth.
  if (!uid) return { enabled: false, time: '20:00' };
  return readJson(NOTIF_KEY(uid)) || { enabled: false, time: '20:00' };
}

export function saveNotifLocal(uid, data) {
  if (!uid) return;
  writeJson(NOTIF_KEY(uid), data);
}

/**
 * Prefixes this app owns in localStorage. Firebase keeps the auth session under
 * `firebase:authUser:…`, so the reset must match on these and nothing else —
 * a blanket localStorage.clear() would sign the user out mid-reset.
 */
const APP_STORAGE_PREFIXES = ['kuroco_', 'kuroco-', 'dashboard_filters_'];

/**
 * Dev reset: drop every local app key (goals cache, profile, notifications,
 * KPI card order, dashboard filters). Returns the keys actually removed so the
 * caller can report what happened instead of claiming success blindly.
 *
 * @returns {string[]}
 */
export function clearLocalAppData() {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  const doomed = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && APP_STORAGE_PREFIXES.some((p) => key.startsWith(p))) doomed.push(key);
  }
  doomed.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* key stays; the caller still sees it in the returned list */
    }
  });
  return doomed;
}

/** Legacy localStorage changelog append (fallback when store/stylist unavailable). */
export function appendChangeLog(uid, entry) {
  const g = loadGoalsLocal(uid) || {};
  const log = Array.isArray(g.changeLog) ? g.changeLog : [];
  saveGoalsLocal(uid, { ...g, changeLog: [entry, ...log] });
}

/**
 * Goals hook — Firestore via API when store+stylist set; localStorage fallback.
 * Returns [goals, setGoals, { changeLog, loading, appendChangeLogApi }].
 */
export function useGoalsConfig(uid, store = null, stylist = null) {
  const [goals, setGoalsState] = useState(() => loadGoalsLocal(uid));
  const [changeLog, setChangeLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const apiEnabled = Boolean(store && stylist);
  const mountedRef = useRef(true);
  // Own record only — a manager edits their own goals and reads the team's.
  // Enforced server-side by can_write_stylist_goals(); checked here too because
  // both writers below fall back to optimistic localStorage on error, which
  // would make a rejected edit look saved while viewing someone else.
  const canEditGoals = useCanEditStylist(stylist);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!uid) {
      setGoalsState(null);
      setChangeLog([]);
      return;
    }
    if (!apiEnabled) {
      setGoalsState(loadGoalsLocal(uid));
      setChangeLog(loadGoalsLocal(uid)?.changeLog || []);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const [goalsRes, logRes] = await Promise.all([
          fetchGoals({ store, stylist }),
          fetchGoalsChangelog({ store, stylist }),
        ]);
        if (cancelled) return;

        const apiGoals = goalsRes.goals;
        setError(null);
        if (apiGoals) {
          setGoalsState(apiGoals);
          saveGoalsLocal(uid, { ...apiGoals, changeLog: undefined });
        } else {
          // No goals on the server for this store+stylist. The local cache is
          // keyed by uid alone, so it may hold a DIFFERENT stylist's goals —
          // never show it as this one's.
          setGoalsState(null);
        }
        setChangeLog(logRes.entries || []);
      } catch (err) {
        if (!cancelled) {
          // Do not silently substitute the uid-keyed local copy: while browsing
          // the team that copy belongs to whoever was loaded last. Report the
          // failure instead of showing another stylist's numbers.
          console.error('[goals] load failed', { store, stylist }, err);
          setError(err?.message || '目標の取得に失敗しました');
          setGoalsState(null);
          setChangeLog([]);
        }
      } finally {
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [uid, store, stylist, apiEnabled]);

  // Sync when another screen (AI chat) patches / refreshes goals
  useEffect(() => {
    const onUpdated = (event) => {
      const next = event?.detail?.goals;
      if (!next || !mountedRef.current) return;
      setGoalsState(next);
      if (uid) saveGoalsLocal(uid, { ...next, changeLog: undefined });
    };
    window.addEventListener(GOALS_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(GOALS_UPDATED_EVENT, onUpdated);
  }, [uid]);

  const setGoals = useCallback(
    async (next, options = {}) => {
      if (!uid) return;
      if (!canEditGoals) return goals;
      const prev = goals ?? loadGoalsLocal(uid) ?? null;
      const merged = typeof next === 'function' ? next(prev || {}) : next;
      setGoalsState(merged);
      saveGoalsLocal(uid, merged);
      notifyGoalsUpdated(merged);

      if (!apiEnabled) return merged;

      const { changelog, patchFields, throwOnError = false } = options;
      const patch = patchFields ?? merged;
      try {
        const res = await patchGoals({
          store,
          stylist,
          patch,
          changelog,
        });
        setError(null);
        if (res.goals) {
          setGoalsState(res.goals);
          saveGoalsLocal(uid, res.goals);
          notifyGoalsUpdated(res.goals);
        }
        if (changelog) {
          const logRes = await fetchGoalsChangelog({ store, stylist });
          setChangeLog(logRes.entries || []);
        }
        // The server's doc carries what the client cannot know (goalsApprovalStatus …)
        if (res.goals) return res.goals;
      } catch (err) {
        // The save failed, so roll the optimistic write back instead of leaving
        // a rejected edit on screen looking saved. Surfaced via meta.error.
        console.error('[goals] save failed', { store, stylist }, err);
        setError(err?.message || '目標の保存に失敗しました');
        setGoalsState(prev);
        if (prev) saveGoalsLocal(uid, prev);
        notifyGoalsUpdated(prev);
        // 初期設定 (one tap = save + next) must not advance on a failed save
        if (throwOnError) throw err;
        return prev;
      }
      return merged;
    },
    [uid, store, stylist, apiEnabled, goals, canEditGoals]
  );

  // 行動 edits → 確認中 submission. Unlike setGoals there is no optimistic write:
  // the live plan must stay the last approved one, so only the server's goals are adopted.
  const submitActions = useCallback(
    async (actions) => {
      if (!uid || !canEditGoals || !apiEnabled) return null;
      try {
        const res = await submitActionsApi({ store, stylist, actions });
        setError(null);
        if (res.goals) {
          setGoalsState(res.goals);
          saveGoalsLocal(uid, res.goals);
          notifyGoalsUpdated(res.goals);
        }
        return res;
      } catch (err) {
        console.error('[goals] submit actions failed', { store, stylist }, err);
        setError(err?.message || '行動プランの保存に失敗しました');
        throw err;
      }
    },
    [uid, store, stylist, apiEnabled, canEditGoals]
  );

  const appendChangeLogApi = useCallback(
    async (entry) => {
      if (!uid) return;
      if (!canEditGoals) return;
      if (!apiEnabled) {
        appendChangeLog(uid, entry);
        setChangeLog((prev) => [entry, ...prev]);
        return;
      }
      try {
        await postGoalsChangelog({
          store,
          stylist,
          kind: entry.kind,
          icon: entry.icon,
          title: entry.title,
          summary: entry.summary || '',
          changes: entry.changes || [],
          by: entry.by,
        });
        const logRes = await fetchGoalsChangelog({ store, stylist });
        setChangeLog(logRes.entries || []);
        setError(null);
      } catch (err) {
        // Was: silently append to the uid-keyed local log, which both hid the
        // failure and could attach the entry to the wrong stylist's history.
        console.error('[goals] changelog append failed', { store, stylist }, err);
        setError(err?.message || '変更履歴の保存に失敗しました');
      }
    },
    [uid, store, stylist, apiEnabled, canEditGoals]
  );

  return [
    goals,
    setGoals,
    {
      changeLog,
      loading,
      appendChangeLogApi,
      submitActions,
      apiEnabled,
      canEditGoals,
      error,
      clearError: () => setError(null),
    },
  ];
}
